<template>
	<div class="modal" id="export-modal" ref="modal" tabindex="-1" role="dialog">
		<div class="modal-dialog" role="document">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">{{ __('Export Report') }}</h5>
					<button type="button" class="close" data-dismiss="modal" aria-label="Close">
						<span aria-hidden="true">&times;</span>
					</button>
				</div>
				<div class="modal-body">
					<div class="d-flex mb-3 justify-content-between">
						<a class="link-option" :class="{ active: openTab == 'export' }" href="#"
							@click.prevent="openTab = 'export'">
							<i class="fas fa-download"></i><span>{{ __('Export Reviews') }}</span>
						</a>
						<a class="link-option" :class="{ active: openTab == 'create' }" href="#"
							@click.prevent="openTab = 'create'">
							<i class="fas fa-pencil-alt"></i><span>{{ __('Schedule Report') }}</span>
						</a>
						<a class="link-option" :class="{ active: openTab == 'view' }" href="#"
							@click.prevent="openTab = 'view'">
							<i class="fas fa-list"></i><span>{{ __('View Scheduled') }}</span>
						</a>
					</div>

					<template v-if="openTab == 'export' && !loading">
						<div>
							<strong>{{ __('Type') }}</strong>
							<div class="mb-3">
								<select v-model="reportType">
									<option value="xls">{{ __('Excel Report') }}</option>
									<option value="pdf">{{ __('PDF Report') }}</option>
								</select>
							</div>
						</div>
						<div>
							<strong>{{ __('Filters') }}</strong>
							<div class="mb-3">
								<ExportReviewsFilters :filters="activeFilters"
									@update-export-date-range="updateExportDateRange" />
							</div>
						</div>
						<div class="mb-3">
							<strong>{{ __('Email') }}</strong>
							<input type="text" class="form-control" placeholder="Enter e-mail to send the report"
								v-model="email">
						</div>
						<strong>{{ __('Reviews') }}</strong>
						<div class="d-flex justify-content-between py-2 border-bottom">
							<div class="text-right">
								<select v-model="exportReviewCount">
									<option :value="estimate">{{ __('All') + ' (' + estimate + ' ' + __('Reviews') + ')' }}</option>
									<option v-for="count in predefinedCountOptions" :value="count">
										{{ __('Last') + ' ' + count + ' ' + __('Reviews') }}
									</option>
								</select>
							</div>
							<button class="btn btn-primary" @click="sendExport"
								:disabled="!exportDateRange.fromDate || !exportDateRange.toDate">
								{{ __('Send Export') }}
							</button>
						</div>
					</template>
					<div class="d-flex justify-content-center">
						<div class="row" v-if="loading">
							<div class="col-md-12">
								<div>
									<i class="fa fa-btn fa-spinner fa-spin"></i>
									{{ __('Loading') }}
								</div>
							</div>
						</div>
					</div>

					<ReviewsScheduleCreate v-if="openTab == 'create'" />
					<ReviewsScheduleView v-if="openTab == 'view'" />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ReviewsScheduleView from './ReviewsScheduleView.vue';
import ReviewsScheduleCreate from './ReviewsScheduleCreate.vue';
import LoadingIndicator from '../LoadingIndicator.vue';
import ExportReviewsFilters from './ExportReviewsFilters.vue';

export default {
	props: ['params', 'showexportdialog', 'showAllSentiment'],

	components: {
		ReviewsScheduleView, ReviewsScheduleCreate, LoadingIndicator, ExportReviewsFilters
	},

	data() {
		return {
			estimating: false,
			estimate: 0,
			reportType: 'pdf',
			openTab: 'export',
			email: Spark.state.user.email,
			teamId: Spark.state.currentTeam.id,
			loading: false,
			exportReviewCount: null, 
			exportDateRange: {
				fromDate: null,
				toDate: null
			}
		}
	},

	computed: {
		currentPayload() {
			const payload = {
				email: this.email,
				reportType: this.reportType,
				exportReviewCount: this.exportReviewCount,
				showAllSentiment: this.showAllSentiment,
				fromDate: this.exportDateRange.fromDate,
				toDate: this.exportDateRange.toDate
			};

			const paramsToInclude = ['language', 'source', 'sentimentCategory', 'reviews', 'responseFilter', 'importance'];

			const filteredParams = Object.entries(this.params)
				.filter(([key, value]) => paramsToInclude.includes(key) && value !== '');

			filteredParams.forEach(([key, value]) => {
				if (key === 'source' && Array.isArray(value)) {
					payload[key] = value.join(',');
				} else {
					payload[key] = value;
				}
			});

			return payload;
		},

		activeFilters() {
			return {
				...this.params,
				showAllSentiment: this.showAllSentiment
			};
		},

		predefinedCountOptions() {
			// Filter predefined counts to ensure they are less than or equal to the estimate
			return [10, 50, 100].filter(count => count <= this.estimate);
		},
	},

	watch: {
		showexportdialog: function () {
			this.toggleModal();
		}
	},

	methods: {
		async getEstimate() {
			this.estimating = true;
			const payload = this.currentPayload;
			try {
				const response = await axios.post(`/api/reviews/${this.teamId}/estimate`, payload);
				this.estimating = false;
				this.estimate = response.data;

				if (!this.exportReviewCount) {
					this.exportReviewCount = this.estimate;
				}
			} catch (error) {
				this.estimating = false;
				console.error('Error during estimate:', error);
			}
		},

		updateExportDateRange(fromDate, toDate) {
            if (toDate && moment(toDate).isBefore(moment(fromDate))) {
                [fromDate, toDate] = [toDate, fromDate];
            }

            this.exportDateRange.fromDate = fromDate;
            this.exportDateRange.toDate = toDate;
            this.exportReviewCount = null;
            if (fromDate && toDate) {
                this.getEstimate();
            }
        },

		toggleModal() {
			let instance = this;
			Vue.nextTick(function () {
				$(instance.$refs.modal).modal('toggle');
				const isOpen = $(instance.$refs.modal).hasClass('show');

				if (isOpen) {
					instance.getEstimate();
				}
			});
		},

		async sendExport() {
			const payload = this.currentPayload;
			this.loading = true;
			try {
				const response = await axios.post(`/api/report/reviews/${this.teamId}`, payload);
				this.loading = false;
				this.showSuccessMessage();
				this.toggleModal();
				this.exportDateRange.fromDate = null;
				this.exportDateRange.toDate = null;
				this.exportReviewCount = null;  
			} catch (error) {
				this.loading = false;
				console.error('Error during report export:', error);
			}
		},

		showSuccessMessage() {
			swal(__('Done!'), __('The report will arrive shortly in the e-mail you entered.'), 'success');
		}
	}
}
</script>
